import Avvvatars from "avvvatars-react";

// ============================================
// 타입 정의
// ============================================

type AvatarSize = "16" | "20" | "24" | "32" | "40";
type AvatarStroke = "none" | "white" | "dark";
type AvatarType = "label" | "shape";

interface AvatarPropsWithValue {
  /** Avvvatars에 전달할 고유 값 (이름, 이메일 등) */
  value: string;
  displayName?: never;
  email?: never;
}

interface AvatarPropsWithUser {
  value?: never;
  /** 사용자 표시 이름 */
  displayName?: string;
  /** 사용자 이메일 */
  email: string;
}

interface AvatarCommonProps {
  /** 아바타 크기 */
  size?: AvatarSize;
  /** 테두리 스타일 */
  stroke?: AvatarStroke;
  /** 표시 타입 (label: 글자, shape: 도형) */
  type?: AvatarType;
}

type AvatarProps = AvatarCommonProps &
  (AvatarPropsWithValue | AvatarPropsWithUser);

// ============================================
// 스타일 매핑
// ============================================

const sizeMap: Record<AvatarSize, number> = {
  "16": 16,
  "20": 20,
  "24": 24,
  "32": 32,
  "40": 40,
};

const borderSizeMap: Record<AvatarSize, number> = {
  "16": 1,
  "20": 1,
  "24": 2,
  "32": 2,
  "40": 3,
};

const strokeColorMap: Record<AvatarStroke, string | undefined> = {
  none: undefined,
  white: "#FFFFFF",
  dark: "#060A23",
};

/** 한글 이름인지 확인 */
const isKoreanName = (name: string): boolean => /[가-힣]/.test(name);

/** displayName 또는 email에서 이니셜 추출 */
export const getAvatarInitial = ({
  displayName,
  email,
}: {
  displayName?: string;
  email: string;
}): string => {
  if (displayName) {
    // 한글 이름: 성 제외 첫 글자 (예: "홍길동" → "길")
    if (isKoreanName(displayName)) {
      return displayName.slice(1, 2) || displayName.charAt(0);
    }
    // 영문 이름: 첫 두 글자 (예: "John Doe" → "Jo")
    return displayName.slice(0, 2);
  }
  return email.split("@")[0]?.slice(0, 2) || "";
};

// ============================================
// Avatar 컴포넌트
// ============================================

export const Avatar = ({
  value,
  displayName,
  email,
  size = "40",
  stroke = "none",
  type = "label",
}: AvatarProps) => {
  const hasBorder = stroke !== "none";

  // displayName/email이 제공되면 이니셜 계산, 아니면 value 사용
  const avatarValue =
    email !== undefined
      ? getAvatarInitial({ displayName, email })
      : (value as string);

  return (
    <Avvvatars
      value={avatarValue}
      size={sizeMap[size]}
      style={type === "label" ? "character" : "shape"}
      border={hasBorder}
      borderSize={hasBorder ? borderSizeMap[size] : undefined}
      borderColor={strokeColorMap[stroke]}
    />
  );
};

export default Avatar;
